import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { BUILD_VERSION } from '../../../../environments/version';

/**
 * Detects new deployments and force-reloads stale browser tabs.
 *
 * Detection is change-based: the first X-App-Version response header seen
 * after page load is the baseline; any different value later means a deploy
 * happened. Before reloading we confirm the new frontend bundle is actually
 * live (version.json differs from the embedded BUILD_VERSION) — the backend
 * deploys minutes before the frontend files, and reloading inside that
 * window would re-baseline the tab on a still-stale bundle.
 *
 * The reload fires on the next NavigationEnd, when the user is already
 * leaving their current screen, so no in-progress form work is lost.
 */
@Injectable({ providedIn: 'root' })
export class VersionWatcherService {
  static readonly VERSION_HEADER = 'X-App-Version';
  private static readonly RELOAD_ATTEMPT_KEY = 'app-version-reload-attempted';

  private readonly router = inject(Router);

  private baselineVersion: string | null = null;
  private updateDetected = false;
  private confirmInFlight = false;

  /** Called once from AppComponent. */
  start(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => void this.checkForSafeReload());
  }

  /** Fed by VersionHeaderInterceptor with every response's version header. */
  notifyBackendVersion(version: string | null): void {
    if (!version) {
      return;
    }

    if (this.baselineVersion === null) {
      this.baselineVersion = version;
      return;
    }

    if (version !== this.baselineVersion) {
      this.updateDetected = true;
    }
  }

  async checkForSafeReload(): Promise<void> {
    if (!this.updateDetected || this.confirmInFlight) {
      return;
    }

    this.confirmInFlight = true;
    try {
      const liveVersion = await this.fetchLiveVersion();
      if (!liveVersion || liveVersion === BUILD_VERSION) {
        // New frontend not fetchable yet — keep updateDetected and retry
        // on the next navigation.
        return;
      }

      if (sessionStorage.getItem(VersionWatcherService.RELOAD_ATTEMPT_KEY) === liveVersion) {
        // Already reloaded once for this version and still stale — avoid a loop.
        return;
      }

      sessionStorage.setItem(VersionWatcherService.RELOAD_ATTEMPT_KEY, liveVersion);
      this.reloadPage();
    } finally {
      this.confirmInFlight = false;
    }
  }

  protected async fetchLiveVersion(): Promise<string | null> {
    try {
      const response = await fetch(`/version.json?cb=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) {
        return null;
      }

      const body: { version?: string } = await response.json();
      return body.version ?? null;
    } catch {
      return null;
    }
  }

  protected reloadPage(): void {
    window.location.reload();
  }
}
